import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useThematicPrompts } from "@/hooks/useThematicPrompts";
import { useGenerationRuns } from "@/hooks/useGenerationRuns";
import { usePipelineConfig } from "@/hooks/usePipelineConfig";
import {
  generateNodes,
  generateContentPlan,
  generateContentFull,
  fillPromptTemplate,
} from "@/services/thematicMapService";
import { hashQuestion, checkDuplicate, checkSemanticDuplicates } from "@/services/deduplicationService";
import { normalizeNodeLanguage } from "@/services/validation/reverseSerpValidator";
import { startLogRun, endLogRun, logPublic } from "@/services/contentEngine/contentLogger";
import { isWorkerModeMapEnabled } from "@/lib/workerMode";
import { enqueueMapStep } from "@/services/queueClient";
import {
  generateMacroAI,
  generateSeedsAI,
  generateNodesBatchAI,
  extractEntitiesAI,
  extractEAVAI,
  generateRelationsAI,
} from "@/services/mapGenerationCore";
import type {
  ThematicMap,
  ThematicSeed,
  ThematicNode,
  ThematicEntity,
  ThematicNodeRelation,
  ThematicNodeContent,
  ThematicWritingProfile,
  PromptStep,
  PromptModel,
  LLMMacroOutput,
  LLMSeedOutput,
  LLMNodeOutput,
  LLMEntityOutput,
  LLMRelationOutput,
  LLMEAVOutput,
  LLMContentPlanOutput,
  LLMContentFullOutput,
  NodePreview,
  BatchNodeResult,
} from "@/types/thematicMaps";

/** Build 5 persona template variables from a writing profile */
function buildPersonaVars(
  profile: ThematicWritingProfile,
  documentRole?: string,
): Record<string, string> {
  const persona_identity = [
    `Autor: ${profile.author_identity}`,
    `Misja: ${profile.mission}`,
    `Odbiorca główny: ${profile.audience_primary}`,
    profile.audience_secondary
      ? `Odbiorca poboczny: ${profile.audience_secondary}`
      : "",
    `Poziom trudności: ${profile.difficulty_level}`,
  ]
    .filter(Boolean)
    .join("\n");

  const persona_style_dna = [
    `Zwracanie się: ${profile.voice_pronouns}`,
    `Zwięzłość: ${profile.verbosity}`,
    `Długość zdań: ${profile.sentence_length}`,
    `Częstość analogii: ${profile.analogy_frequency}`,
    profile.analogy_domain
      ? `Dziedzina analogii: ${profile.analogy_domain}`
      : "",
    profile.preferred_phrases.length > 0
      ? `Preferowane zwroty: ${profile.preferred_phrases.join(", ")}`
      : "",
    profile.banned_phrases.length > 0
      ? `Zakazane zwroty: ${profile.banned_phrases.join(", ")}`
      : "",
    profile.language_rules ? `Reguły językowe: ${profile.language_rules}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const h2Pattern =
    documentRole && profile.h2_patterns[documentRole]
      ? `Schemat H2 (${documentRole}): ${profile.h2_patterns[documentRole]}`
      : "";
  const persona_structure = [
    profile.required_sections.length > 0
      ? `Sekcje obowiązkowe: ${profile.required_sections.join(", ")}`
      : "",
    h2Pattern,
    profile.faq_rules ? `FAQ: ${profile.faq_rules}` : "",
    `Tabele: ${profile.use_tables}`,
    profile.media_preferences ? `Media: ${profile.media_preferences}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const persona_evidence = [
    profile.evidence_style ? `Styl dowodzenia: ${profile.evidence_style}` : "",
    `Siła twierdzeń: ${profile.claim_strength}`,
    profile.disclaimer_template
      ? `Disclaimer: ${profile.disclaimer_template}`
      : "",
    profile.example_policy ? `Przykłady: ${profile.example_policy}` : "",
    profile.measurement_policy ? `Pomiary: ${profile.measurement_policy}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const persona_glossary = [
    `Tryb: ${profile.glossary_mode}`,
    profile.abbreviation_rule ? `Skróty: ${profile.abbreviation_rule}` : "",
    profile.core_entities.length > 0
      ? `Encje: ${profile.core_entities.join(", ")}`
      : "",
    profile.units_standards ? `Jednostki: ${profile.units_standards}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return {
    persona_identity,
    persona_style_dna,
    persona_structure,
    persona_evidence,
    persona_glossary,
  };
}

export const useMapGenerator = (mapId: string | undefined) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { getPrompt } = useThematicPrompts();
  const { createRun, updateRun } = useGenerationRuns(mapId);
  const { getConfig } = usePipelineConfig();
  const [generating, setGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState<PromptStep | null>(null);

  const logRun = useCallback(
    async (
      step: PromptStep,
      promptUsed: string,
      inputJson: Record<string, unknown>,
      startTime: number,
      result: {
        success: boolean;
        data?: any;
        error?: string;
        tokensInput?: number;
        tokensOutput?: number;
        modelUsed?: string;
      },
    ) => {
      if (!user || !mapId) return;

      await createRun({
        user_id: user.id,
        map_id: mapId,
        step,
        input_json: inputJson,
        prompt_used: promptUsed,
        model: result.modelUsed || "claude-sonnet-5",
        output_json: result.data || null,
        tokens_input: result.tokensInput || 0,
        tokens_output: result.tokensOutput || 0,
        cost_usd:
          ((result.tokensInput || 0) * 0.003 +
            (result.tokensOutput || 0) * 0.015) /
          1000,
        status: result.success ? "completed" : "failed",
        error_message: result.error || null,
        duration_ms: Date.now() - startTime,
      });
    },
    [user, mapId, createRun],
  );

  const runMacroGeneration = useCallback(
    async (
      map: ThematicMap,
      websiteName?: string,
      websiteUrl?: string,
    ): Promise<LLMMacroOutput | null> => {
      const prompt = getPrompt("macro_generation");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu macro_generation",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("macro_generation");
      const startTime = Date.now();

      const coreResult = await generateMacroAI({
        map,
        websiteName,
        websiteUrl,
        prompt,
      });

      await logRun(
        "macro_generation",
        coreResult.filledPrompt,
        coreResult.vars,
        startTime,
        {
          success: coreResult.success,
          data: coreResult.data,
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (!coreResult.success) {
        toast({
          title: "Błąd generowania",
          description: coreResult.error,
          variant: "destructive",
        });
        return null;
      }

      toast({ title: "Sukces", description: "Macro wygenerowane z AI" });
      return coreResult.data!;
    },
    [getPrompt, toast, logRun],
  );

  const runSeedGeneration = useCallback(
    async (map: ThematicMap): Promise<LLMSeedOutput[] | null> => {
      // Worker mode: enqueue do kolejki, worker procesuje w tle.
      // Zwracamy null — UI nie dostaje seedow synchronicznie, zamiast tego
      // ładuje je z DB (thematic_seeds z status='draft') po realtime update.
      if (await isWorkerModeMapEnabled()) {
        try {
          const res = await enqueueMapStep("seeds", map.id);
          toast({
            title: "Generowanie w tle (worker)",
            description: `Job #${res.queueRowId.slice(0, 8)} — mozesz zamknac karte. Drafty pojawia sie po zakonczeniu.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({
            title: "Blad worker enqueue",
            description: msg,
            variant: "destructive",
          });
        }
        return null;
      }

      const prompt = getPrompt("seed_generation");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu seed_generation",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("seed_generation");
      const startTime = Date.now();

      const coreResult = await generateSeedsAI({ map, prompt });

      await logRun(
        "seed_generation",
        coreResult.filledPrompt,
        coreResult.vars,
        startTime,
        {
          success: coreResult.success,
          data: coreResult.data,
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (!coreResult.success) {
        toast({
          title: "Błąd generowania",
          description: coreResult.error,
          variant: "destructive",
        });
        return null;
      }

      toast({
        title: "Sukces",
        description: `Wygenerowano ${coreResult.data!.length} seeds`,
      });
      return coreResult.data!;
    },
    [getPrompt, toast, logRun],
  );

  const runNodeGeneration = useCallback(
    async (
      map: ThematicMap,
      seed: ThematicSeed,
      existingNodes: ThematicNode[],
      allSeeds: ThematicSeed[],
    ): Promise<NodePreview[] | null> => {
      // Worker mode: enqueue zamiast synchronizowanego awaitu. Worker procesuje.
      if (await isWorkerModeMapEnabled()) {
        try {
          const res = await enqueueMapStep("nodes", map.id, { seedId: seed.id });
          toast({
            title: `Worker: generuje nody dla "${seed.name}"`,
            description: `Job #${res.queueRowId.slice(0, 8)} — mozesz zamknac karte. Drafty pojawia sie po zakonczeniu.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({
            title: "Blad worker enqueue",
            description: msg,
            variant: "destructive",
          });
        }
        return null;
      }

      const prompt = getPrompt("node_generation");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu node_generation",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("node_generation");
      const startTime = Date.now();

      startLogRun(`nodes-${seed.id}`, { mapId: map.id, seedId: seed.id, seedName: seed.name, phase: "node_generation" });
      logPublic("NODES", `START — seed: "${seed.name}" (${existingNodes.length} istniejacych)`);

      const existingContext = existingNodes
        .map(
          (n) =>
            `- ${n.title} (/${n.slug}) [${n.document_role}] Q: ${n.canonical_question}`,
        )
        .join("\n");

      const vars = {
        macro_statement: map.macro_statement,
        seed_name: seed.name,
        seed_slug: seed.slug,
        seed_intent_family: (seed.intent_family || []).join(", "),
        existing_nodes_in_map: existingContext || "(brak)",
        language: map.language || "pl",
        seed_type: seed.seed_type || "",
        scope_coverage: Array.isArray(seed.scope_coverage)
          ? seed.scope_coverage.join(", ")
          : "",
        other_seed_names: allSeeds
          .filter((s) => s.id !== seed.id)
          .map((s) => s.name)
          .join(", "),
      };

      const filledPrompt = fillPromptTemplate(prompt.prompt_template, vars);
      const result = await generateNodes(
        prompt.prompt_template,
        vars,
        prompt.model as PromptModel,
      );

      await logRun("node_generation", filledPrompt, vars, startTime, result);

      setGenerating(false);
      setCurrentStep(null);

      if (!result.success) {
        toast({
          title: "Błąd generowania",
          description: result.error,
          variant: "destructive",
        });
        return null;
      }

      // Normalize node language — simplify, split 2-intent nodes
      let normalizedNodes = result.data!;
      try {
        const rawPreviews: NodePreview[] = result.data!.map((node) => ({
          ...node,
          canonical_question_hash: hashQuestion(node.canonical_question),
          duplicate_level: "unique" as const,
        }));
        console.log(`[Nodes] Single: ${rawPreviews.length} raw candidates`);
        rawPreviews.slice(0, 3).forEach((n, i) => console.log(`[Nodes]   raw[${i}]: "${n.canonical_question}"`));

        const normalized = await normalizeNodeLanguage(rawPreviews, map.language || "pl");
        if (normalized.length > 0) {
          normalizedNodes = normalized.map((n) => ({
            title: n.title,
            slug: n.slug,
            document_role: n.document_role,
            primary_intent: n.primary_intent,
            primary_entity: n.primary_entity,
            canonical_question: n.canonical_question,
            required_entities: n.required_entities,
          }));
          console.log(`[Nodes] Normalized: ${normalizedNodes.length} nodes`);
          normalizedNodes.slice(0, 3).forEach((n, i) => console.log(`[Nodes]   norm[${i}]: "${n.canonical_question}"`));
        }
      } catch (normErr) {
        console.warn("[Nodes] Normalize failed:", normErr);
      }

      // 3-kolorowy preview (anti-dupe)
      const existingForCheck = existingNodes.map((n) => ({
        canonical_question_hash: n.canonical_question_hash,
        primary_entity_raw: n.primary_entity_raw,
        document_role: n.document_role,
        title: n.title,
        slug: n.slug,
      }));

      const previews: NodePreview[] = normalizedNodes.map((node) => {
        const hash = hashQuestion(node.canonical_question);
        const dupeCheck = checkDuplicate(
          node.canonical_question,
          node.primary_entity,
          node.document_role,
          existingForCheck,
        );

        return {
          ...node,
          canonical_question_hash: hash,
          duplicate_level: dupeCheck.level,
          duplicate_reason: dupeCheck.reason,
          duplicate_match_slug: dupeCheck.matchSlug,
        };
      });

      let dupes = previews.filter(
        (p) => p.duplicate_level === "duplicate",
      ).length;
      let warns = previews.filter(
        (p) => p.duplicate_level === "warning",
      ).length;

      // Semantic dedup via LLM prompt (for "unique" nodes with similar content)
      const dedupPrompt = getPrompt("deduplication");
      if (dedupPrompt && previews.filter(p => p.duplicate_level === 'unique').length > 1) {
        try {
          const { checkedPairs, upgraded } = await checkSemanticDuplicates(
            previews,
            dedupPrompt.prompt_template,
            dedupPrompt.model as PromptModel,
          );
          if (upgraded > 0) {
            // Recount after semantic dedup
            dupes = previews.filter(p => p.duplicate_level === 'duplicate').length;
            warns = previews.filter(p => p.duplicate_level === 'warning').length;
          }
        } catch {
          // Semantic dedup failed — continue with heuristic results only
        }
      }

      if (dupes > 0 || warns > 0) {
        toast({
          title: "Uwaga — duplikaty",
          description: `${previews.length} nodes, ${dupes} duplikatów, ${warns} ostrzeżeń`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sukces",
          description: `Wygenerowano ${previews.length} nodes`,
        });
      }

      logPublic("NODES", `OK — ${previews.length} nodes (${dupes} dupli, ${warns} warn)`);
      endLogRun({ phase: "nodes", count: previews.length, duplicates: dupes, warnings: warns, duration_s: Math.round((Date.now() - startTime) / 1000) });

      return previews;
    },
    [getPrompt, toast, logRun],
  );

  // --- Batch: generowanie nodes dla wielu seeds naraz ---
  const runBatchNodeGeneration = useCallback(
    async (
      map: ThematicMap,
      seedList: ThematicSeed[],
      existingNodes: ThematicNode[],
      onProgress?: (current: number, total: number, seedName: string) => void,
    ): Promise<BatchNodeResult[]> => {
      // Worker mode: enqueue 1 batch job z pelna lista seedIds.
      // Worker procesuje SEKWENCYJNIE (parallel z pipelineConfig, default 1)
      // tak zeby kazdy kolejny seed widzial growing dedup pool z poprzednich.
      if (await isWorkerModeMapEnabled()) {
        if (seedList.length === 0) {
          toast({ title: "Nic do zrobienia", description: "Brak seedow bez nodow" });
          return [];
        }
        const nodeConfig = getConfig("node_generation");
        const parallel = Math.max(1, Number(nodeConfig.parallel_batches ?? 1));
        try {
          const res = await enqueueMapStep("nodes_batch", map.id, {
            seedIds: seedList.map((s) => s.id),
            parallel,
          });
          toast({
            title: `Worker: batch ${seedList.length} seedow`,
            description: `Job #${res.queueRowId.slice(0, 8)} — sekwencyjnie z growing dedup (parallel=${parallel}). Mozesz zamknac karte.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({
            title: "Blad worker batch enqueue",
            description: msg,
            variant: "destructive",
          });
        }
        return []; // UI subskrybuje realtime na thematic_nodes — drafty pojawia sie auto
      }

      const prompt = getPrompt("node_generation");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu node_generation",
          variant: "destructive",
        });
        return [];
      }
      const dedupPrompt = getPrompt("deduplication");
      const nodeConfig = getConfig("node_generation");

      setGenerating(true);
      setCurrentStep("node_generation");
      const batchStartTime = Date.now();

      const coreResult = await generateNodesBatchAI({
        map,
        seedList,
        existingNodes,
        nodePrompt: prompt,
        dedupPrompt: dedupPrompt ?? undefined,
        config: {
          batch_size: nodeConfig.batch_size,
          parallel_batches: nodeConfig.parallel_batches || 1,
        },
        onProgress,
      });

      const results: BatchNodeResult[] = coreResult.data || [];
      const totalNodes = (coreResult.meta?.totalNodes as number) ?? 0;
      const errors = (coreResult.meta?.errors as number) ?? 0;

      // Aggregate-level DB log (per-seed logging skipped — same as pre-refactor where per-seed
      // logRun used empty filledPrompt/vars so it didn't carry sensible info anyway).
      await logRun(
        "node_generation",
        coreResult.filledPrompt,
        coreResult.vars,
        batchStartTime,
        {
          success: coreResult.success,
          data: { results: results.length, totalNodes, errors },
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (totalNodes === 0 && errors > 0) {
        toast({
          title: "Błąd",
          description: `Wszystkie seeds (${errors}) zakończyły się błędem`,
          variant: "destructive",
        });
      } else if (errors > 0) {
        toast({
          title: "Częściowy sukces",
          description: `${totalNodes} nodes z ${seedList.length} seeds (${errors} błędów)`,
        });
      } else {
        toast({
          title: "Sukces",
          description: `Wygenerowano ${totalNodes} nodes z ${seedList.length} seeds`,
        });
      }

      return results;
    },
    [getPrompt, getConfig, toast, logRun],
  );

  // Config from Settings → Pipeline Config (user-editable)
  const entityConfig = getConfig("entity_extraction");
  const ENTITY_BATCH_SIZE = entityConfig.batch_size;
  const PARALLEL_BATCHES = entityConfig.parallel_batches;

  const runEntityExtraction = useCallback(
    async (
      map: ThematicMap,
      nodes: ThematicNode[],
      onProgress?: (msg: string) => void,
    ): Promise<LLMEntityOutput[] | null> => {
      // Worker mode: enqueue + return null (UI subskrybuje realtime na thematic_entities)
      if (await isWorkerModeMapEnabled()) {
        try {
          const res = await enqueueMapStep("entities", map.id);
          toast({
            title: "Worker: ekstrakcja encji",
            description: `Job #${res.queueRowId.slice(0, 8)} — encje + linking do nodow w tle. Mozesz zamknac karte.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({ title: "Blad worker enqueue", description: msg, variant: "destructive" });
        }
        return null;
      }

      const prompt = getPrompt("entity_extraction");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu entity_extraction",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("entity_extraction");
      const startTime = Date.now();

      const coreResult = await extractEntitiesAI({
        map,
        nodes,
        prompt,
        config: {
          batch_size: ENTITY_BATCH_SIZE,
          parallel_batches: PARALLEL_BATCHES,
        },
        onProgress,
      });

      await logRun(
        "entity_extraction",
        coreResult.filledPrompt,
        coreResult.vars,
        startTime,
        {
          success: coreResult.success,
          data: coreResult.data,
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (!coreResult.success || !coreResult.data || coreResult.data.length === 0) {
        toast({
          title: "Brak wyników",
          description:
            coreResult.error || "Nie udało się wyekstrahować żadnych encji",
          variant: "destructive",
        });
        return null;
      }

      const batchCount = (coreResult.meta?.batches as number) ?? 1;
      toast({
        title: "Sukces",
        description:
          batchCount > 1
            ? `Wyekstrahowano ${coreResult.data.length} encji (${batchCount} batchy)`
            : `Wyekstrahowano ${coreResult.data.length} encji`,
      });

      return coreResult.data;
    },
    [getPrompt, ENTITY_BATCH_SIZE, PARALLEL_BATCHES, toast, logRun],
  );

  const relConfig = getConfig("linking");
  const RELATION_BATCH_SIZE = relConfig.batch_size;

  const runRelationGeneration = useCallback(
    async (
      map: ThematicMap,
      nodes: ThematicNode[],
      onProgress?: (msg: string) => void,
    ): Promise<LLMRelationOutput[] | null> => {
      if (await isWorkerModeMapEnabled()) {
        try {
          const res = await enqueueMapStep("linking", map.id);
          toast({
            title: "Worker: generowanie relacji",
            description: `Job #${res.queueRowId.slice(0, 8)} — batche w tle. Mozesz zamknac karte.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({ title: "Blad worker enqueue", description: msg, variant: "destructive" });
        }
        return null;
      }

      const prompt = getPrompt("linking");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu linking",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("linking");
      const startTime = Date.now();

      const coreResult = await generateRelationsAI({
        map,
        nodes,
        prompt,
        config: {
          batch_size: RELATION_BATCH_SIZE,
          parallel_batches: relConfig.parallel_batches,
        },
        onProgress,
      });

      await logRun(
        "linking",
        coreResult.filledPrompt,
        coreResult.vars,
        startTime,
        {
          success: coreResult.success,
          data: coreResult.data,
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (!coreResult.success || !coreResult.data || coreResult.data.length === 0) {
        toast({
          title: "Brak wyników",
          description:
            coreResult.error || "Nie udało się wygenerować żadnych relacji",
          variant: "destructive",
        });
        return null;
      }

      const batchCount = (coreResult.meta?.batches as number) ?? 1;
      toast({
        title: "Sukces",
        description:
          batchCount > 1
            ? `Wygenerowano ${coreResult.data.length} relacji (${batchCount} batchy)`
            : `Wygenerowano ${coreResult.data.length} relacji`,
      });

      return coreResult.data;
    },
    [getPrompt, RELATION_BATCH_SIZE, relConfig.parallel_batches, toast, logRun],
  );

  const eavConfig = getConfig("eav_extraction");
  const EAV_BATCH_SIZE = eavConfig.batch_size;

  const runEAVExtraction = useCallback(
    async (
      map: ThematicMap,
      entities: ThematicEntity[],
      nodes: ThematicNode[],
      onProgress?: (msg: string) => void,
    ): Promise<LLMEAVOutput[] | null> => {
      if (await isWorkerModeMapEnabled()) {
        try {
          const res = await enqueueMapStep("eav", map.id);
          toast({
            title: "Worker: ekstrakcja EAV",
            description: `Job #${res.queueRowId.slice(0, 8)} — atrybuty per encja w tle. Mozesz zamknac karte.`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "unknown";
          toast({ title: "Blad worker enqueue", description: msg, variant: "destructive" });
        }
        return null;
      }

      const prompt = getPrompt("eav_extraction");
      if (!prompt) {
        toast({
          title: "Błąd",
          description: "Brak aktywnego promptu eav_extraction",
          variant: "destructive",
        });
        return null;
      }

      if (entities.length === 0) {
        toast({
          title: "Błąd",
          description: "Brak encji — najpierw wyekstrahuj encje",
          variant: "destructive",
        });
        return null;
      }

      setGenerating(true);
      setCurrentStep("eav_extraction");
      const startTime = Date.now();

      const coreResult = await extractEAVAI({
        map,
        entities,
        nodes,
        prompt,
        config: {
          batch_size: EAV_BATCH_SIZE,
          parallel_batches: eavConfig.parallel_batches,
        },
        onProgress,
      });

      await logRun(
        "eav_extraction",
        coreResult.filledPrompt,
        coreResult.vars,
        startTime,
        {
          success: coreResult.success,
          data: coreResult.data,
          error: coreResult.error,
          tokensInput: coreResult.tokensInput,
          tokensOutput: coreResult.tokensOutput,
          modelUsed: coreResult.modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      const totalAttrs = (coreResult.meta?.totalAttrs as number) ?? 0;
      const batchCount = (coreResult.meta?.batches as number) ?? 1;

      if (!coreResult.success || totalAttrs === 0) {
        toast({
          title: "Brak wyników",
          description:
            coreResult.error ||
            "Nie udało się wyekstrahować żadnych atrybutów",
          variant: "destructive",
        });
        return null;
      }

      const processedResults = coreResult.data || [];
      toast({
        title: "Sukces",
        description:
          batchCount > 1
            ? `Wyekstrahowano ${totalAttrs} atrybutów dla ${processedResults.length} encji (${batchCount} batchy)`
            : `Wyekstrahowano ${totalAttrs} atrybutów dla ${processedResults.length} encji`,
      });

      return processedResults;
    },
    [getPrompt, EAV_BATCH_SIZE, eavConfig.parallel_batches, toast, logRun],
  );

  const runContentPlanGeneration = useCallback(
    async (
      map: ThematicMap,
      nodes: ThematicNode[],
      relations: ThematicNodeRelation[],
      profile: ThematicWritingProfile,
      onProgress?: (msg: string) => void,
    ): Promise<{ nodeId: string; plan: LLMContentPlanOutput }[] | null> => {
      if (!profile.content_plan_prompt) {
        toast({
          title: "Błąd",
          description:
            "Profil nie ma promptu content_plan. Uzupełnij go w zakładce Prompty profilu.",
          variant: "destructive",
        });
        return null;
      }

      // Model fallback: global prompt model or default
      const globalPrompt = getPrompt("content_plan");
      const model = (globalPrompt?.model || "claude-sonnet-5") as PromptModel;

      setGenerating(true);
      setCurrentStep("content_plan");
      const startTime = Date.now();

      const results: { nodeId: string; plan: LLMContentPlanOutput }[] = [];
      let totalTokensIn = 0;
      let totalTokensOut = 0;
      let modelUsed = "";

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        onProgress?.(`Node ${i + 1}/${nodes.length}: "${node.title}"...`);

        // Build related_nodes from ALL other nodes (with optional relation info)
        const otherNodes = nodes
          .filter((n) => n.id !== node.id)
          .map((n) => {
            const rel = relations.find(
              (r) =>
                (r.from_node_id === node.id && r.to_node_id === n.id) ||
                (r.to_node_id === node.id && r.from_node_id === n.id),
            );
            const relInfo = rel ? ` [relacja: ${rel.relation_type}]` : "";
            return `${n.title} (/${n.slug})${relInfo}`;
          });

        // Find seed name
        const seedName = (node as any).seed_name || "";

        // Build persona vars from profile
        const personaVars = buildPersonaVars(profile, node.document_role);

        const vars = {
          node_title: node.title,
          canonical_question: node.canonical_question,
          document_role: node.document_role,
          primary_entity: node.primary_entity_raw || "",
          required_entities: (node.required_entities_raw || []).join(", "),
          seed_name: seedName,
          related_nodes:
            otherNodes.length > 0 ? otherNodes.join("\n") : "(brak)",
          language: map.language || "pl",
          ...personaVars,
        };

        const result = await generateContentPlan(
          profile.content_plan_prompt,
          vars,
          model,
        );

        if (result.success && result.data) {
          results.push({ nodeId: node.id, plan: result.data });
          totalTokensIn += result.tokensInput || 0;
          totalTokensOut += result.tokensOutput || 0;
          modelUsed = result.modelUsed || modelUsed;
        } else {
          toast({
            title: `Plan nieudany: ${node.title}`,
            description: result.error,
            variant: "destructive",
          });
        }
      }

      // Log full run with persona snapshot
      const personaSnapshot = buildPersonaVars(profile);
      const filledPrompt = fillPromptTemplate(profile.content_plan_prompt, {
        node_title: "(batch)",
      });
      await logRun(
        "content_plan",
        filledPrompt,
        {
          nodes_count: nodes.length,
          _persona_snapshot: {
            active_profile_id: profile.id,
            profile_name: profile.name,
            ...personaSnapshot,
          },
          _prompt_source: "profile",
        },
        startTime,
        {
          success: results.length > 0,
          data: { plans_count: results.length },
          tokensInput: totalTokensIn,
          tokensOutput: totalTokensOut,
          modelUsed,
        },
      );

      setGenerating(false);
      setCurrentStep(null);

      if (results.length === 0) {
        toast({
          title: "Brak wyników",
          description: "Nie udało się wygenerować żadnych planów",
          variant: "destructive",
        });
        return null;
      }

      toast({
        title: "Sukces",
        description: `Wygenerowano ${results.length}/${nodes.length} planów treści`,
      });
      return results;
    },
    [getPrompt, toast, logRun],
  );

  const runContentFullGeneration = useCallback(
    async (
      map: ThematicMap,
      node: ThematicNode,
      nodeContent: ThematicNodeContent,
      profile: ThematicWritingProfile,
      onProgress?: (msg: string) => void,
    ): Promise<LLMContentFullOutput | null> => {
      if (!profile.content_full_prompt) {
        toast({
          title: "Błąd",
          description:
            "Profil nie ma promptu content_full. Uzupełnij go w zakładce Prompty profilu.",
          variant: "destructive",
        });
        return null;
      }

      if (nodeContent.content_status !== "plan_ready") {
        toast({
          title: "Błąd",
          description: "Node nie ma planu — najpierw wygeneruj plan",
          variant: "destructive",
        });
        return null;
      }

      // Model fallback: global prompt model or default
      const globalPrompt = getPrompt("content_full");
      const model = (globalPrompt?.model || "claude-sonnet-5") as PromptModel;

      setGenerating(true);
      setCurrentStep("content_full");
      const startTime = Date.now();

      onProgress?.(`Generowanie treści dla "${node.title}"...`);

      // Build persona vars from profile
      const personaVars = buildPersonaVars(profile, node.document_role);

      // Find seed name
      const seedName = (node as any).seed_name || "";

      const vars = {
        node_title: node.title,
        canonical_question: node.canonical_question,
        document_role: node.document_role,
        primary_entity: node.primary_entity_raw || "",
        required_entities: (node.required_entities_raw || []).join(", "),
        seed_name: seedName,
        seo_title: nodeContent.seo_title || node.title,
        meta_description: nodeContent.meta_description || "",
        opening_answer: nodeContent.opening_answer || "",
        h2_outline: JSON.stringify(nodeContent.h2_outline || []),
        key_points: JSON.stringify(nodeContent.key_points || []),
        faq: JSON.stringify(nodeContent.faq || []),
        do_not_include: JSON.stringify(nodeContent.do_not_include || []),
        internal_links: JSON.stringify(nodeContent.internal_links || []),
        language: map.language || "pl",
        ...personaVars,
      };

      const filledPrompt = fillPromptTemplate(
        profile.content_full_prompt,
        vars,
      );
      const result = await generateContentFull(
        profile.content_full_prompt,
        vars,
        model,
      );

      // Log with persona snapshot
      await logRun(
        "content_full",
        filledPrompt,
        {
          ...vars,
          _persona_snapshot: {
            active_profile_id: profile.id,
            profile_name: profile.name,
            ...personaVars,
          },
          _prompt_source: "profile",
        },
        startTime,
        result,
      );

      setGenerating(false);
      setCurrentStep(null);

      if (!result.success) {
        toast({
          title: "Błąd generowania treści",
          description: result.error,
          variant: "destructive",
        });
        return null;
      }

      toast({
        title: "Sukces",
        description: `Wygenerowano treść dla "${node.title}"`,
      });
      return result.data!;
    },
    [getPrompt, toast, logRun],
  );

  return {
    generating,
    currentStep,
    runMacroGeneration,
    runSeedGeneration,
    runNodeGeneration,
    runBatchNodeGeneration,
    runEntityExtraction,
    runEAVExtraction,
    runRelationGeneration,
    runContentPlanGeneration,
    runContentFullGeneration,
  };
};
